import React, { useEffect, useState } from 'react'
import { Assignation } from '../../context/types'
import { HistogramBars } from './components/Histogram/HistogramBars'
import { DragOverlay } from '@dnd-kit/core'
import { isLandscape, isMobileAll, isMobilePortrait } from '../../utils'
import { useBinsStore } from './lib/state'
import { Button, useTheme } from '@mui/material'
import { getOverfilledBuckets } from './lib/helper'
import { maxBucketCapacities } from './lib/constants'
import { Icons } from '../../assets/icons'
import { isArabic, useTranslation } from '../../i18n/useTranslation'
import { YesNoModal } from './components/YesNoModal'
import { QTitle } from './components/QTitle'
import { StmList } from './components/StmList'
import { RankingStm } from './components/rankingStm'
import _ from 'lodash'
import { DndContainer } from './components/Dnd/DndContainer'
import { useCtxParams } from '../../context/useCommonCtx'

export const Overview = () => {
    const binStoreObj = useBinsStore()
    const muiTheme = useTheme()
    const t = useTranslation()
    const params = useCtxParams()
    
    const {
        selectedBucket,
        finalBuckets,
        onMoveStmDistro,
        nBucket,
        hasMidBucketRanking,
        onClickToNext,
        //
        yesNoModalProps,
        closeYesNoModal,
    } = useBinsStore()
    const itemsPerBar = finalBuckets.map((bucket) => bucket.length)
    const middleIndex = Math.floor(finalBuckets.length / 2)
    const selectedIndex = selectedBucket ?? middleIndex
    const effectiveNBuckets =
        nBucket === 7 && hasMidBucketRanking === true ? 9 : nBucket

    useEffect(() => {
        if (binStoreObj.selectedBucket === null) {
            binStoreObj.setSelectedBucket(middleIndex)
        }
    }, [binStoreObj.selectedBucket, binStoreObj.setSelectedBucket, middleIndex])

    const [draggingId, setDraggingId] = useState<string | null>(null)
    const draggedStm = draggingId
        ? _.flattenDeep(finalBuckets).find((stm) => stm.valueKey === draggingId)
        : null

    const lastIndex = finalBuckets.length - 1
    let displayIndices: [number, number, number]
    if (selectedIndex === 0) {
        displayIndices = [-1, 0, 1]
    } else if (selectedIndex === lastIndex) {
        displayIndices = [lastIndex - 1, lastIndex, -1]
    } else {
        let prev = selectedIndex - 1
        let next = selectedIndex + 1
        if (effectiveNBuckets === 7) {
            if (prev === 3 || prev === 5) {
                prev--
            }
            if (next === 3 || next === 5) {
                next++
            }
        }
        displayIndices = [prev, selectedIndex, next]
    }
    const [leftIdx, centerIdx, rightIdx] = displayIndices

    const leftBucketItems = leftIdx >= 0 ? finalBuckets[leftIdx] || [] : []
    const centerBucketItems =
        centerIdx >= 0 ? finalBuckets[centerIdx] || [] : []
    const rightBucketItems = rightIdx >= 0 ? finalBuckets[rightIdx] || [] : []

    const [left, center, right] = buildTitles(
        displayIndices,
        itemsPerBar,
        maxBucketCapacities[effectiveNBuckets]
    )

    const overfilledBuckets = getOverfilledBuckets(
        finalBuckets,
        effectiveNBuckets
    )
    console.debug('overfilledBuckets', overfilledBuckets)
    const onClose = null

    const handleDragEnd = (
        itemId: string,
        destinationId: string,
        sourceId: string,
        destinationIndex?: number
    ) => {
        setDraggingId(null)
        onMoveStmDistro(itemId, sourceId, destinationId, destinationIndex)
    }

    return (
        <DndContainer
            multiContainer
            disabled={binStoreObj.reflectionMode !== 1}
            onDragStart={(itemId, sourceId) => {
                setDraggingId(itemId)
            }}
            // FIXME use onDragOver={handleDragOver} instead of onDragEnd?
            onDragEnd={handleDragEnd}
        >
            <YesNoModal
                // FIXME use only props
                {...yesNoModalProps}
                open={!!yesNoModalProps?.open}
                onClose={() => closeYesNoModal()}
            />
            {/* FIXME [h-full flex-grow] to test iOS (was h-screen) */}
            <div className="flex flex-col px-2 gap-1 h-full flex-grow bg-white">
                <div className="flex justify-between items-center">
                    {onClose ? (
                        <Button
                            onClick={onClose}
                            size="small"
                            // color="default"
                        >
                            <Icons.CloseIcon />
                        </Button>
                    ) : (
                        <div />
                    )}

                    <QTitle title={t('reflect_current_view_title')} />

                    {/* <span className={isMobilePortrait() ? 'text-xs mr-1 font-bold' : 'text-lg mr-1 font-bold'}>
                        {t('reflect_current_view_title')}
                    </span> */}

                    <Button
                        sx={
                            isMobileAll()
                                ? {
                                      padding: 0,
                                  }
                                : {}
                        }
                        onClick={() => onClickToNext(t)}
                        size={isMobileAll() ? 'medium' : 'large'}
                        variant="contained"
                    >
                        {t('yes')}
                    </Button>
                </div>

                <HistogramBars
                    hasHintScale
                    className="flex-shrink-0"
                    height={'10dvh'}
                    maxItemsPerBar={maxBucketCapacities[effectiveNBuckets]}
                    itemsPerBar={itemsPerBar}
                    exceedingBars={overfilledBuckets}
                    selectedBar={selectedIndex}
                    secondarySelectedBars={[leftIdx, rightIdx]}
                    onSelectBar={binStoreObj.setSelectedBucket}
                    boxedBars={displayIndices.filter((idx) => idx >= 0)}
                    // barTitles={[]}
                />
                <div
                    className="grid grid-cols-3"
                    style={{ direction: 'ltr' }} // arabic or not, keep this direction here
                >
                    <h3
                        style={{
                            color: overfilledBuckets.includes(leftIdx)
                                ? 'red'
                                : 'inherit',
                            direction: isArabic(params) ? 'rtl' : 'ltr'
                        }}
                        className={`text-sm font-bold my-0 text-center rounded`}

                    >
                        {left.title}
                    </h3>
                    <h3
                        style={{
                            color: overfilledBuckets.includes(centerIdx)
                                ? 'red'
                                : 'inherit',
                        }}
                        className={`text-sm font-bold my-0 text-center rounded`}
                    >
                        {center.title}
                    </h3>
                    <h3
                        style={{
                            color: overfilledBuckets.includes(rightIdx)
                                ? 'red'
                                : 'inherit',
                        }}
                        className={`text-sm font-bold my-0 text-center rounded`}
                    >
                        {right.title}
                    </h3>
                </div>
                {/* h-0: parent grid/flex container to ensure it doesn't force its children to be taller than the available space . Was: min-h-0 h-0 . This is now holding the scrollbar (overflow-y-auto) */}
                <div
                    className="flex-1 grid grid-cols-3 gap-1 overflow-y-auto"
                    style={{ direction: 'ltr' }} // arabic or not, keep this direction here
                >
                    <StmList
                        isWarning={overfilledBuckets.includes(leftIdx)}
                        id={left.id}
                        disabled={leftIdx === -1}
                        bgColor={
                            leftIdx === -1
                                ? muiTheme.palette.background.default
                                : muiTheme.palette.primary.main
                        }
                        // title={left.title}
                        items={leftBucketItems}
                    />
                    <StmList
                        isWarning={overfilledBuckets.includes(centerIdx)}
                        id={center.id}
                        // title={center.title}
                        items={centerBucketItems}
                        bgColor={muiTheme.palette.primary.dark}
                    />
                    <StmList
                        isWarning={overfilledBuckets.includes(rightIdx)}
                        id={right.id}
                        // title={right.title}
                        disabled={rightIdx === -1}
                        bgColor={
                            rightIdx === -1
                                ? muiTheme.palette.background.default
                                : muiTheme.palette.primary.main
                        }
                        items={rightBucketItems}
                    />
                </div>
            </div>
            <DragOverlay
                style={{
                    opacity: 0.4,
                }}
            >
                {draggedStm && (
                    <RankingStm
                        key={draggedStm.valueKey}
                        stm={draggedStm}
                        compact
                        landscape={isLandscape()}
                        // sourceContainerId={draggingSourceId ?? ''}
                        withIcon={!isMobilePortrait()}
                    />
                )}
            </DragOverlay>
        </DndContainer>
    )
}

export const buildTitles = (
    indices: number[],
    itemsPerBar: number[],
    maxBucketCapacities: number[]
): { id: string; title: string }[] => {
    const output = []
    for (const index of indices) {
        if (index < 0) {
            output.push({ id: '', title: '' })
        } else {
            output.push({
                id: `Bucket ${index + 1}`,
                title: `${itemsPerBar[index]}/${maxBucketCapacities[index]}`,
            })
        }
    }
    console.debug('buildTitles', {
        indices,
        itemsPerBar,
        maxBucketCapacities,
        output,
    })
    return output
}
